import { Component, OnDestroy, OnInit, inject, viewChild } from '@angular/core';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { Observable, forkJoin, Subject, takeUntil } from 'rxjs';
import { ApiService } from '../core/http/api.service';
import { Document } from '../models/classes/document.class';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { PagedResponse } from '../models/classes/paged-response.class';
import { DocumentQuery } from '../models/classes/document-query.class';
import { DateFormatPipe } from '../pipes/date-format.pipe';
import { MatChipsModule } from '@angular/material/chips';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { GcmDatePickerComponent, GcmLoadingService, GcmNotificationService, Notification, NotificationType } from '@gcm/gcm-ui-angular';
import { MatOptionModule } from '@angular/material/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { MatInputModule } from '@angular/material/input';
import { ActivityTrackingService } from '../services/activity-tracking.service';
import { InvestorOption, InvestorApiResponse } from '../models/interfaces/investor-and-portfolio.interface';
import { PortfoliosMeta } from '../models/classes/portfolios-meta.class';

@Component({
    selector: 'app-document-review',
    imports: [
        MatPaginatorModule,
        MatTableModule,
        MatChipsModule,
        MatSelectModule,
        MatDatepickerModule,
        FormsModule,
        ReactiveFormsModule,
        CommonModule,
        MatSortModule,
        MatIconModule,
        MatOptionModule,
        MatButtonModule,
        NgxMatSelectSearchModule,
        MatInputModule,
        GcmDatePickerComponent
    ],
    templateUrl: './document-review.component.html',
    styleUrls: ['./document-review.component.scss']
})
export class DocumentReviewComponent implements OnInit, OnDestroy {
  private api = inject(ApiService);
  private loading = inject(GcmLoadingService);
  private formBuilder = inject(FormBuilder);
  private dateFormat = inject(DateFormatPipe);
  private activityTracking = inject(ActivityTrackingService);
  private notificationService = inject(GcmNotificationService);

  documents = new PagedResponse<Document>();
  displayedColumns: string[] = ['investorName', 'portfolioName', 'status', 'error', 'reportingPeriod', 'createdOn', 'retry'];
  dataSource: Document[] = [];
  readonly sort = viewChild(MatSort);
  readonly paginator = viewChild(MatPaginator);
  searchForm!: FormGroup;
  dateRangeList: string[] = ["Last 7 days", "Last 30 days", "This Month", "This Quarter", "This Year"];
  readonly statusSelect = viewChild<MatSelect>('statusSelect');
  readonly documentDateSelect = viewChild<MatSelect>('documentDateSelect');
  readonly reportGenerationDateSelect = viewChild<MatSelect>('reportGenerationDateSelect');
  statusFilter: string[] = [];
  statusOptions = [
    {
      value: "generated",
      display: "Generated"
    },
    {
      value: "generatedWithMissingData",
      display: "Generated With Missing Data"
    },
    {
      value: "generatedWithErrors",
      display: "Generated With Errors"
    },
    {
      value: "failedToGenerate",
      display: "Failed To Generate"
    }
  ];
  documentDatesFilter: string[] = [];
  reportGenerationDateFilters = '';
  reportGenerationStartDate!: string | null;
  reportGenerationEndDate!: string | null;
  selectedReportGenerationDate = '';
  readonly documentPicker = viewChild<GcmDatePickerComponent>('documentPicker');
  readonly reportGenerationPicker = viewChild<GcmDatePickerComponent>('reportGenerationPicker');
  allFilters: string[] = [];
  customReportGenerationDateRange = '';
  dateToday = new Date().toISOString();
  reportGenerationDateRange = new FormGroup({
    start: new FormControl(this.dateToday),
    end: new FormControl(this.dateToday)
  });
  pageSizes: number[] = [10, 25, 50, 75, 100];
  page = 0;
  pageSize = 10;
  matSortEvent!: Sort;
  readonly investorSelect = viewChild<MatSelect>('investorSelect');
  investorFilters: number[] = [];
  readonly portfolioSelect = viewChild<MatSelect>('portfolioSelect');
  portfolioFilters: number[] = [];
  fullPortfolioList: PortfoliosMeta[] = [];
  fullInvestorList: InvestorOption[] = [];
  portfolioFiltersNames: string[] = [];
  investorFiltersNames: string[] = [];
  searchedInvestorList: InvestorOption[] = [];
  searchedPortfolioList: PortfoliosMeta[] = [];
  investorsByPortfolios: InvestorOption[] = [];
  portfoliosByInvestors: PortfoliosMeta[] = [];
  selectedInvestors: number[] = [];
  selectedPortfolios: number[] = [];
  investorSearch: FormControl<string | null> = new FormControl<string>('');
  portfolioSearch: FormControl<string | null> = new FormControl<string>('');
  _onDestroy = new Subject<void>();
  url = new URL(window.location.href);
  documentDateString = "Document Date: ";
  generationDateString = "Generation Date: ";
  gmtGenerationStartDate: Date | null = new Date();
  gmtGenerationEndDate: Date | null = new Date();

  ngOnInit(): void {
    this.searchForm = this.formBuilder.group({
      statusFilter: [],
      documentDatesFilter: [],
      reportGenerationDateFilter: [],
      investors: [],
      portfolios: []
    })
    this.getPortfolioAndInvestorData();
    this.applyExistingParams();
    this.listenToSearchChanges();
    this.activityTracking.landedOnPage('Document Review');
  }

  onSortChange(sort: Sort) {
    this.page = 0;
    this.matSortEvent = sort;
    this.getPagedResponse();
  }

  onPaginateChange(event: PageEvent) {
    this.page = event.pageIndex;
    this.pageSize = event.pageSize;
    this.getPagedResponse();
  }

  ngOnDestroy(): void {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  applyExistingParams() {
    this.statusFilter = this.url.searchParams.get('statusfilter')?.split(',') || [];
    this.form.statusFilter.setValue(this.statusFilter);
    this.documentDatesFilter = this.url.searchParams.get('documentdatesfilter')?.split(',') || [];
    this.form.documentDatesFilter.setValue(this.documentDatesFilter);
    this.reportGenerationDateFilters = this.url.searchParams.get('reportgenerationdatefilter') || '';
    this.form.reportGenerationDateFilter.setValue(this.reportGenerationDateFilters);
    this.reportGenerationStartDate = this.url.searchParams.get('reportgenerationstartdate');
    this.gmtGenerationStartDate = this.reportGenerationStartDate ? new Date(this.reportGenerationStartDate) : null;
    this.reportGenerationEndDate = this.url.searchParams.get('reportgenerationenddate');
    this.gmtGenerationEndDate = this.reportGenerationEndDate ? new Date(this.reportGenerationEndDate) : null;
    this.getAllFilters();
  }

  get form() {
    return this.searchForm.controls;
  }

  getPagedResponse() {
    const loadingTask = 'getPagedResponse';
    this.loading.addAsyncTask(loadingTask);
    const sort = this.matSortEvent?.active;
    const sortDirection = this.matSortEvent?.direction !== '' ? this.matSortEvent?.direction : null;
    const documentQuery = {
      page: this.page,
      pageSize: this.pageSize,
      documentDates: this.documentDatesFilter,
      reportGenerationStartDate: this.reportGenerationStartDate?.slice(0, 10),
      reportGenerationEndDate: this.reportGenerationEndDate?.slice(0, 10),
      sort: sort,
      sortDesc: sortDirection && sortDirection === 'desc',
      portfolioIds: this.portfolioFilters,
      investorIds: this.investorFilters,
      status: this.statusFilter
    } as DocumentQuery;

    (this.api.getDocumentReports(documentQuery) as Observable<PagedResponse<Document>>).subscribe({
      next: (res: PagedResponse<Document>) => {
        this.documents = res;
        this.documents.items.forEach(item => {
          (item as Document & { displayStatus: string }).displayStatus = this.getFullNameStatus(item.status)
        })
        this.loading.resolveAsyncTask(loadingTask);
        this.assignToDataSource(this.documents);
      },
      error: () => {
        this.loading.resolveAsyncTask(loadingTask);
      }
    })
  }

  retryDocumentGeneration(id: number) {
    const loadingTask = `retryDocument${id}`;
    this.loading.addAsyncTask(loadingTask);
    this.api.retryDocumentGeneration(id).subscribe({
      next: (res: string) => {
        this.notificationService.notify(new Notification(res, 'Document Generation', NotificationType.info)); 
        this.loading.resolveAsyncTask(loadingTask);
      },
      error: (err) => {
        this.notificationService.notifyError(err);
        this.loading.resolveAsyncTask(loadingTask);
      }
    })
  }

  assignToDataSource(documents: PagedResponse<Document>) {
    this.dataSource = documents.items;
    setTimeout(() => {
      const paginator = this.paginator();
      if (paginator) {
        paginator.pageIndex = this.page;
        paginator.length = documents.totalCount;
      }
    });
  }

  getFullNameStatus(value: string) {
    const status = this.statusOptions.filter(status => status.value == value)[0];
    return status.display;
  }

  //filters methods:
  async applyFilters() {
    const statusFilterValue = this.form.statusFilter.value;
    const investorsFilterValue = this.selectedInvestors;
    const portfoliosFilterValue = this.selectedPortfolios;
    const selectedDocumentDatePeriod = this.form.documentDatesFilter.value;
    const selectedReportGenerationDatePeriod = this.form.reportGenerationDateFilter.value;

    if (statusFilterValue?.length > 0) {
      this.statusFilter = statusFilterValue;
    }
    this.documentDatesFilter = selectedDocumentDatePeriod;
    this.reportGenerationDateFilters = selectedReportGenerationDatePeriod;
    if (investorsFilterValue?.length > 0) {
      this.investorFilters = investorsFilterValue;
    }
    if (portfoliosFilterValue?.length > 0) {
      this.portfolioFilters = portfoliosFilterValue;
    }
    this.page = 0;
    this.setFilterParams()
    this.getAllFilters();
  }

  setFilterParams() {
    const filterParams: [string, (string | number)[] | null][] = [];
    filterParams.push(["reportGenerationStartDate", this.reportGenerationStartDate ? [this.reportGenerationStartDate] : null])
    filterParams.push(["reportGenerationEndDate", this.reportGenerationEndDate ? [this.reportGenerationEndDate] : null])
    filterParams.push(["statusFilter", this.statusFilter])
    filterParams.push(["documentDatesFilter", this.documentDatesFilter])
    filterParams.push(["reportGenerationDateFilter", this.reportGenerationDateFilters && this.reportGenerationDateFilters != '' ? [this.reportGenerationDateFilters] : null])
    filterParams.push(["investors", this.investorFilters])
    filterParams.push(["portfolios", this.portfolioFilters])
    this.url.search = '';
    filterParams.forEach(filter => {
      const type = filter[0];
      this.url.searchParams.delete(type);
      const values = filter[1]
      if (values && values.length > 0) {
        this.url.searchParams.append(type, values.join(','));
      }
    })
    history.replaceState({}, '', this.url.href)
  }

  async clearFilters() {
    if (this.allFilters.length > 0 || this.investorFiltersNames.length > 0 || this.portfolioFiltersNames.length > 0) {
      this.searchForm.reset();
      this.statusFilter = [];
      this.investorFilters = [];
      this.portfolioFilters = [];
      this.allFilters = [];
      this.portfolioFiltersNames = [];
      this.investorFiltersNames = [];
      this.selectedInvestors = [];
      this.selectedPortfolios = [];
      this.documentDatesFilter = [];
      this.reportGenerationDateFilters = '';
      this.customReportGenerationDateRange = '';
      this.reportGenerationStartDate = null;
      this.reportGenerationEndDate = null;
      this.page = 0;
      this.searchedInvestorList = this.fullInvestorList;
      this.searchedPortfolioList = this.fullPortfolioList;
      this.setFilterParams();
      this.getPagedResponse();
    }
  }

  getAllFilters() {
    this.allFilters = []
    if (this.statusFilter.length > 0) {
      const statusFilterByDisplay = this.statusOptions.filter((status) => this.statusFilter.includes(status.value)).map((status) => status.display);
      this.allFilters.push(...statusFilterByDisplay)
    }

    if (this.documentDatesFilter && this.documentDatesFilter.length > 0) {
      this.documentDatesFilter.forEach(dateFilter => {
        this.allFilters.push(this.documentDateString + dateFilter);
      })
    }

    if (this.reportGenerationDateFilters && this.reportGenerationDateFilters != '') {
      if (this.reportGenerationDateFilters === 'Custom') {
        const splitStartDate = (this.reportGenerationStartDate as string).split('-')
        const splitEndDate = (this.reportGenerationEndDate as string).split('-')
        const startYear = splitStartDate[0]
        const startMonth = splitStartDate[1]
        const startDay = splitStartDate[2].split(' ')[0]
        const endYear = splitEndDate[0]
        const endMonth = splitEndDate[1]
        const endDay = splitEndDate[2].split(' ')[0]
        this.customReportGenerationDateRange = this.generationDateString + startMonth + "/" + startDay + '/' + startYear + " - " + endMonth + "/" + endDay + '/' + endYear
        this.allFilters.push(this.customReportGenerationDateRange)
      } else {
        this.allFilters.push(this.generationDateString + this.reportGenerationDateFilters)
      }
    }
    this.getInvestorFilterNames();
    this.getPortfolioFilterNames();
    this.getPagedResponse();
  }

  removeFilter(removedFilter: string) {
    //remove error filter
    const removedStatusFilter = this.statusOptions.filter((status) => status.display === removedFilter)[0];
    this.statusFilter = this.statusFilter.filter((status) => status != removedStatusFilter.value);
    this.searchForm.patchValue({ statusFilter: this.statusFilter });

    //remove document date filter
    const removedDocumentDateFilter = removedFilter.includes(this.documentDateString) ? removedFilter.replace(this.documentDateString, '') : undefined;
    if (removedDocumentDateFilter && this.documentDatesFilter?.includes(removedDocumentDateFilter)) {
      this.documentDatesFilter = this.documentDatesFilter.filter(date => date != removedDocumentDateFilter);
      this.searchForm.patchValue({ documentDatesFilter: this.documentDatesFilter });
    }

    //remove report generation date filter
    if (removedFilter === this.reportGenerationDateFilters || removedFilter === this.customReportGenerationDateRange || removedFilter.includes(this.generationDateString)) {
      this.reportGenerationDateFilters = ''
      this.customReportGenerationDateRange = ''
      this.reportGenerationStartDate = this.reportGenerationEndDate = null;
      this.searchForm.patchValue({ reportGenerationDateFilter: '' })
    }

    this.applyFilters();
  }

  //portfolio and investor filters methods:
  getPortfolioAndInvestorData() {
    const getPortfolioData = this.api.getPortfoliosMetadata();
    const getInvestorData = this.api.getInvestors();

    forkJoin([getPortfolioData, getInvestorData]).subscribe({
      next: ([portfolioData, investorData]) => {
        this.fullPortfolioList = this.searchedPortfolioList = this.portfoliosByInvestors = portfolioData;
        this.fullInvestorList = this.searchedInvestorList = this.investorsByPortfolios = (investorData as unknown as InvestorApiResponse[]).map(investor => ({
          ...investor,
          relatedPortfolios: investor.relatedPortfolios ?? []
        }));
        this.getCurrentPortfolioFilters();
        this.getCurrentInvestorFilters();
        //get document list
        this.getPagedResponse();
      },
      error: error => {
        console.log("Error:", error)
        //get document list anyways, without the portfolio and investors filters
        this.getPagedResponse();
      }
    });
  }

  getCurrentPortfolioFilters() {
    this.portfolioFilters = this.url.searchParams.get('portfolios')?.split(',').map(Number) || [];
    if (this.portfolioFilters) {
      this.portfolioFilters.forEach(id => {
        this.portfolioOptionClick(id)
      })
      this.form.portfolios.setValue(this.portfolioFilters);
      this.getPortfolioFilterNames();
    }
  }

  getCurrentInvestorFilters() {
    this.investorFilters = this.url.searchParams.get('investors')?.split(',').map(Number) || [];
    if (this.investorFilters) {
      this.investorFilters.forEach(id => {
        this.investorOptionClick(id)
      })
      this.form.investors.setValue(this.investorFilters);
      this.getInvestorFilterNames();
    }
  }

  getInvestorFilterNames() {
    if (this.investorFilters.length > 0) {
      this.investorFiltersNames = [];
      //get investor filter names to display as selected filters
      this.investorFiltersNames = this.fullInvestorList.filter((investor) => this.investorFilters.includes(investor.id)).map((investor) => investor.name);
    }
  }

  getPortfolioFilterNames() {
    if (this.portfolioFilters.length > 0) {
      this.portfolioFiltersNames = [];
      //get portfolio filter names to display as selected filters
      this.portfolioFiltersNames = this.fullPortfolioList.filter((portfolio) => this.portfolioFilters.includes(portfolio.id)).map((portfolio) => portfolio.name);
    }
  }

  removeOrAddIdToSelection(id: number, arr: number[]) {
    const index = arr.indexOf(id);
    if (index > -1) {
      arr.splice(index, 1);
    } else {
      arr.push(id)
    }
    return arr;
  }

  investorOptionClick(id: number) {
    this.selectedInvestors = this.removeOrAddIdToSelection(id, this.selectedInvestors);
    if (this.selectedInvestors && this.selectedInvestors.length > 0) {
      this.getPortfoliosByInvestors(this.selectedInvestors.toString())
    } else {
      this.searchedPortfolioList = this.portfoliosByInvestors = this.fullPortfolioList;
    }
  }

  getPortfoliosByInvestors(investorIds: string) {
    const investorIdsArray = investorIds.split(",").map(Number);
    const filteredList: PortfoliosMeta[] = [];
    investorIdsArray.forEach((investorId: number) => {
      this.fullPortfolioList.forEach(portfolio => {
        if (portfolio.relatedInvestors.includes(investorId)) {
          if (!filteredList.includes(portfolio)) {
            filteredList.push(portfolio)
          }
        }
      })
    })
    this.searchedPortfolioList = this.portfoliosByInvestors = filteredList;
  }

  portfolioOptionClick(id: number) {
    this.selectedPortfolios = this.removeOrAddIdToSelection(id, this.selectedPortfolios);
    if (this.selectedPortfolios && this.selectedPortfolios.length > 0) {
      this.getInvestorsByPortfolios(this.selectedPortfolios.toString());
    } else {
      this.searchedInvestorList = this.investorsByPortfolios = this.fullInvestorList;
    }
  }

  getInvestorsByPortfolios(portfolioIds: string) {
    const portfolioIdsArray = portfolioIds.split(",").map(Number);
    const filteredList: InvestorOption[] = [];
    portfolioIdsArray.forEach((portfolioId: number) => {
      this.fullInvestorList.forEach(investor => {
        if (investor.relatedPortfolios?.includes(portfolioId)) {
          if (!filteredList.includes(investor)) {
            filteredList.push(investor)
          }
        }
      })
    })
    this.searchedInvestorList = this.investorsByPortfolios = filteredList;
  }

  removeInvestorFilter(removedFilter: string) {
    const removedInvestorFilterId = this.fullInvestorList.filter((investor) => investor.name === removedFilter)[0].id;
    this.investorFiltersNames = this.investorFiltersNames.filter(filter => filter != removedFilter);
    this.investorFilters = this.fullInvestorList.filter((investor) => this.investorFiltersNames.includes(investor.name)).map((investor) => investor.id);
    this.searchForm.patchValue({ investors: this.investorFilters });
    this.investorOptionClick(removedInvestorFilterId);
    this.applyFilters();
  }

  removePortfolioFilter(removedFilter: string) {
    const removedPortfolioFilterId = this.fullPortfolioList.filter((portfolio) => portfolio.name === removedFilter)[0].id;
    this.portfolioFiltersNames = this.portfolioFiltersNames.filter(filter => filter != removedFilter);
    this.portfolioFilters = this.fullPortfolioList.filter((portfolio) => this.portfolioFiltersNames.includes(portfolio.name)).map((portfolio) => portfolio.id);
    this.searchForm.patchValue({ portfolios: this.portfolioFilters });
    this.portfolioOptionClick(removedPortfolioFilterId);
    this.applyFilters();
  }

  listenToSearchChanges() {
    // listen for search field value changes
    this.investorSearch.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.dropDownSearch(this.investorSearch.value ?? '', "investor");
      });
    this.portfolioSearch.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.dropDownSearch(this.portfolioSearch.value ?? '', "portfolio");
      });
  }

  // Dropdown Search                                                                                                                              
  dropDownSearch(query: string, type: string) {
    const result = this.selectValue(query, type);
    if (type == 'investor') { this.searchedInvestorList = result as InvestorOption[]; }
    if (type == 'portfolio') { this.searchedPortfolioList = result as PortfoliosMeta[]; }
  }

  // Dropdown Select value
  selectValue(query: string, type: string): (InvestorOption | PortfoliosMeta)[] {
    let searchOptions: (InvestorOption | PortfoliosMeta)[] = [];
    if (type === 'investor') { searchOptions = this.investorsByPortfolios; }
    if (type === 'portfolio') {
      searchOptions = this.portfoliosByInvestors;
      return searchOptions.filter((element) => {
        const portfolio = element as PortfoliosMeta;
        return portfolio.name.toLowerCase().includes(query.toLowerCase()) || portfolio.ticker.toLowerCase().includes(query.toLowerCase());
      });
    }
    const result = searchOptions.filter(element => element.name.toLowerCase().includes(query.toLowerCase()));
    return result;
  }

  dateRangePicker(type: string) {
    if (type === 'document') {
      this.documentDateSelect()?.open();
    }
    else {
      this.reportGenerationDateSelect()?.open();
    }
    this.setStartAndEndDate();
  }

  setStartAndEndDate() {
    this.reportGenerationStartDate = this.reportGenerationDateRange.value.start ?? null;
    this.reportGenerationEndDate = this.reportGenerationDateRange.value.end ?? null;
  }

  updateGenerationDateFilter(e: string | string[] | Date | Date[] | (string | Date)[] | { start: string | Date; end: string | Date; type: string } | undefined) {
    if (!e || typeof e !== 'object' || Array.isArray(e) || !('type' in e)) return;
    this.form.reportGenerationDateFilter.setValue(e.type);
    this.reportGenerationStartDate = this.dateFormat.transform(e.start instanceof Date ? e.start : new Date(e.start), 'yyyy-MM-dd HH:mm:ss');
    this.reportGenerationEndDate = this.dateFormat.transform(e.end instanceof Date ? e.end : new Date(e.end), 'yyyy-MM-dd HH:mm:ss');
  }

  updateDocumentDatesFilter(e: string | string[] | Date | Date[] | (string | Date)[] | { start: string | Date; end: string | Date; type: string } | undefined) {
    if (!e || !Array.isArray(e) || e.length === 0) return;
    const dates = e as (string | Date)[];
    if (dates && dates.length > 0) {
      this.form.documentDatesFilter.setValue(dates.map((date) => this.dateFormat.transform(new Date(date as string | Date), 'MM/dd/yyyy')));
      this.documentDatesFilter = dates.map((date) => this.dateFormat.transform(new Date(date as string | Date), 'yyyy-MM-dd HH:mm:ss') ?? '');
      this.applyFilters();
    } else if (this.documentDatesFilter.length > 0) {
      this.form.documentDatesFilter.setValue([]);
      this.documentDatesFilter = [];
      this.applyFilters();
    }
  }
}
